import { markRaw, shallowReactive } from 'vue';

// The task outlives its panels. A single notice moves into the active native
// dialog, and only a visible inline progress panel takes its place.
export const viewProgressPresence = shallowReactive({ inlineVisible: 0, host: null });
const panels = new Map();
const hosts = new Set();

export function inlineProgressVisible(element) {
  if (!element?.isConnected || element.closest('[inert], [hidden], [aria-hidden="true"]')) return false;
  const doc = element.ownerDocument;
  const modal = [...doc.querySelectorAll('dialog:modal')].at(-1);
  if (modal && !modal.contains(element)) return false;
  for (let node = element; node; node = node.parentElement) {
    const style = doc.defaultView.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) < .5) return false;
  }
  const rect = element.getBoundingClientRect();
  const width = Math.min(rect.right, doc.documentElement.clientWidth) - Math.max(rect.left, 0);
  const height = Math.min(rect.bottom, doc.documentElement.clientHeight) - Math.max(rect.top, 0);
  if (width < Math.min(120, rect.width / 2) || height < Math.min(40, rect.height / 2)) return false;
  // Overflowing composers can leave their progress below the scrollport.
  for (let node = element.parentElement; node; node = node.parentElement) {
    const style = doc.defaultView.getComputedStyle(node);
    if (node === doc.body || node === doc.documentElement) break;
    if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
      const clip = node.getBoundingClientRect();
      if (Math.min(rect.bottom, clip.bottom) - Math.max(rect.top, clip.top) < Math.min(40, rect.height / 2)) return false;
    }
    if (style.position === 'fixed' || node === modal) break;
  }
  return rect.width > 0 && rect.height > 0;
}

function refreshPresence() {
  viewProgressPresence.inlineVisible = [...panels.values()].filter(element => inlineProgressVisible(element)).length;
}

export function registerInlineProgress(element) {
  const id = Symbol('inline progress');
  panels.set(id, element);
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(refreshPresence, { threshold: [0, .5, 1] }) : null;
  observer?.observe(element);
  const attributes = typeof MutationObserver === 'function' ? new MutationObserver(refreshPresence) : null;
  for (let node = element; node; node = node.parentElement) {
    attributes?.observe(node, { attributes: true, attributeFilter: ['inert', 'hidden', 'class', 'style', 'open', 'aria-hidden'] });
  }
  refreshPresence();
  return {
    refresh: refreshPresence,
    dispose() {
      observer?.disconnect();
      attributes?.disconnect();
      panels.delete(id);
      refreshPresence();
    },
  };
}

export function registerViewNoticeHost(element) {
  hosts.add(element);
  viewProgressPresence.host = markRaw(element);
  refreshPresence();
  return () => {
    hosts.delete(element);
    viewProgressPresence.host = [...hosts].at(-1) || null;
    refreshPresence();
  };
}
